import { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Space, Tag, Popconfirm, Empty, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { APIClient } from '../../helpers/api_helper';
import { intentFieldLabel } from '../../Common/intentFieldLabels';
import { ATTEMPT_STATUS_META, ROW_STATUS_META } from './constants';

const api = new APIClient();

/**
 * Every import attempt, finished or not.
 *
 * Completed ones stay listed rather than disappearing: "did I already import
 * January?" is the question this screen exists to answer, and an attempt that
 * vanishes the moment it is done cannot answer it.
 */
const AttemptsList = ({ onOpen, onNew }) => {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/staging_batches/')
      .then((resp) => setAttempts(resp?.results || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = (id) => api
    .delete(`/api/staging_batches/${id}/`, null, { successMsg: 'Poskus izbrisan' })
    .then(load);

  const columns = [
    {
      title: 'Datoteka', dataIndex: 'source_ref',
      render: (value, row) => (
        <Space>
          <FileExcelOutlined />
          <Typography.Link onClick={() => onOpen(row.id)}>
            {value || row.source_kind_title}
          </Typography.Link>
        </Space>
      ),
    },
    {
      title: 'Začeto', dataIndex: 'time_created', width: 160,
      render: (value) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : ''),
    },
    { title: intentFieldLabel('account'), dataIndex: 'account_title', width: 150 },
    {
      title: 'Stanje', dataIndex: 'status', width: 150,
      render: (status) => {
        const meta = ATTEMPT_STATUS_META[status] || { color: 'default', label: status };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Vrstice', dataIndex: 'counts', width: 280,
      render: (counts) => (
        <Space size={4} wrap>
          {Object.entries(ROW_STATUS_META).map(([key, meta]) => (
            counts?.[key]
              ? <Tag key={key} color={meta.color}>{meta.label}: {counts[key]}</Tag>
              : null
          ))}
        </Space>
      ),
    },
    { title: 'Vir', dataIndex: 'source_kind_title', width: 150 },
    {
      title: '', key: 'actions', width: 120,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => onOpen(row.id)}>Odpri</Button>
          <Popconfirm
            title="Izbriši poskus?"
            description="Neshranjene vrstice bodo izgubljene. Že shranjene transakcije ostanejo."
            okText="Izbriši" cancelText="Prekliči"
            onConfirm={() => remove(row.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Poskusi uvoza"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={onNew}>
          Nov uvoz
        </Button>
      }
    >
      <Table
        size="small"
        loading={loading}
        columns={columns}
        dataSource={attempts}
        rowKey="id"
        pagination={false}
        scroll={{ x: 'max-content' }}
        onRow={(row) => ({ onDoubleClick: () => onOpen(row.id) })}
        locale={{
          emptyText: (
            <Empty
              image={<FileExcelOutlined style={{ fontSize: 42 }} />}
              description="Ni še nobenega uvoza. Začnite z &quot;Nov uvoz&quot;."
            />
          ),
        }}
      />
    </Card>
  );
};

export default AttemptsList;
